import { Component, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractDialogResultData } from '@next/core-lib/dialog';

import { SoilAnalysisDialogModel } from './soil-analysis-dialog.model';
import { SoilAnalysis } from '../../models/soil-analysis.models';

@Component({
  selector: 'app-soil-analysis-dialog',
  templateUrl: './soil-analysis-dialog.component.html',
  styleUrls: ['./soil-analysis-dialog.component.scss'],
  standalone: false,
})
export class SoilAnalysisDialogComponent extends AbstractFormDialog<SoilAnalysis> {
  data = inject<SoilAnalysisDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<SoilAnalysisDialogComponent, AbstractDialogResultData<SoilAnalysis>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  formGroup = this.fb.group({
    description: [this.data.soilAnalysis.description, [Validators.required, Validators.minLength(1), Validators.max(1000)]],
    characteristics: this.fb.group({
      carbonToNitrogenRatio: [this.data.soilAnalysis.characteristics.carbonToNitrogenRatio, [Validators.min(0), Validators.max(100)]],
      deliverableNitrogen: [this.data.soilAnalysis.characteristics.deliverableNitrogen, [Validators.min(0), Validators.max(500)]],
      totalNitrogen: [this.data.soilAnalysis.characteristics.totalNitrogen, [Validators.min(0), Validators.max(99999999)]],
      minNitrogen: [this.data.soilAnalysis.characteristics.minNitrogen, [Validators.min(0), Validators.max(99999999)]],
      phosphorusAmmoniumLactate: [this.data.soilAnalysis.characteristics.phosphorusAmmoniumLactate, [Validators.min(0), Validators.max(500)]],
      phosphorusNumber: [this.data.soilAnalysis.characteristics.phosphorusNumber, [Validators.min(0), Validators.max(500)]],
      plantAvailablePhosphorus: [this.data.soilAnalysis.characteristics.plantAvailablePhosphorus, [Validators.min(0), Validators.max(99999999)]],
      potassiumNumber: [this.data.soilAnalysis.characteristics.potassiumNumber, [Validators.min(0), Validators.max(200)]],
      plantAvailableMagnesium: [this.data.soilAnalysis.characteristics.plantAvailableMagnesium, [Validators.min(0), Validators.max(99999999)]],
      calciumCarbonate: [this.data.soilAnalysis.characteristics.calciumCarbonate, [Validators.min(0), Validators.max(100)]],
      organicMatter: [this.data.soilAnalysis.characteristics.organicMatter, [Validators.min(0), Validators.max(100)]],
      soilAcidityNir: [this.data.soilAnalysis.characteristics.soilAcidityNir, [Validators.min(0), Validators.max(15)]],
      soilAcidity: [this.data.soilAnalysis.characteristics.soilAcidity, [Validators.min(0), Validators.max(15)]],
      ammoniaStock: [this.data.soilAnalysis.characteristics.ammoniaStock, [Validators.min(0), Validators.max(250)]],
      nitrateStock: [this.data.soilAnalysis.characteristics.nitrateStock, [Validators.min(0), Validators.max(250)]],
      plantAvailablePotassium: [this.data.soilAnalysis.characteristics.plantAvailablePotassium, [Validators.min(0), Validators.max(1000)]],
      plantAvailableCalcium: [this.data.soilAnalysis.characteristics.plantAvailableCalcium, [Validators.min(0), Validators.max(2500)]],
      calciumStock: [this.data.soilAnalysis.characteristics.calciumStock, [Validators.min(0), Validators.max(10000)]],
      copperStock: [this.data.soilAnalysis.characteristics.copperStock, [Validators.min(0), Validators.max(2500)]],
      ferroStock: [this.data.soilAnalysis.characteristics.ferroStock, [Validators.min(0), Validators.max(25000)]],
      cationExchangeCapacity: [this.data.soilAnalysis.characteristics.cationExchangeCapacity, [Validators.min(0), Validators.max(100)]],
      calciumFilling: [this.data.soilAnalysis.characteristics.calciumFilling, [Validators.min(0), Validators.max(100)]],
      magnesiumFilling: [this.data.soilAnalysis.characteristics.magnesiumFilling, [Validators.min(0), Validators.max(100)]],
      potassiumFilling: [this.data.soilAnalysis.characteristics.potassiumFilling, [Validators.min(0), Validators.max(100)]],
      sodiumFilling: [this.data.soilAnalysis.characteristics.sodiumFilling, [Validators.min(0), Validators.max(100)]],
      soilAvailablePotassium: [this.data.soilAnalysis.characteristics.soilAvailablePotassium, [Validators.min(0), Validators.max(99999999)]],
      potassiumStock: [this.data.soilAnalysis.characteristics.potassiumStock, [Validators.min(0)]],
      cecClayHumus: [this.data.soilAnalysis.characteristics.cecClayHumus, [Validators.min(0)]],
    }),
  });

  confirm() {
    super.confirm({
      ...this.data.soilAnalysis,
      ...this.formGroup.getRawValue(),
    });
  }
}
